import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { EnquiryForm } from "@/components/enquiry-form";
import { FIRM, faqSchema } from "@/lib/firm";

const FAQS = [
  {
    q: "What do I need to have ready before I sell my business in Queensland?",
    a: "Three to five years of financials, a current profit and loss and balance sheet, the lease and any option terms, plant and equipment lists with finance details, employee records including accrued leave, licences and permits, key supplier and customer contracts, and the business name and intellectual property registrations. Buyers ask for all of it during due diligence, and gaps in the paperwork are the most common reason a price gets renegotiated late.",
  },
  {
    q: "Should the sale be structured as an asset sale or a share sale?",
    a: "Most small Queensland sales are asset sales — the buyer takes the plant, stock, goodwill, business name and lease without inheriting the company's history. Share sales can be better for a seller on capital gains tax, but buyers price in the risk of inheriting everything the company has ever done. Get accounting and legal advice on structure before you agree terms, because the structure drives the price, the tax and the warranties.",
  },
  {
    q: "How do I make sure GST is not added to my sale price?",
    a: "A business sold as a going concern can be GST-free, but only if the contract says so, the buyer is registered for GST at settlement, and everything necessary for the continued operation of the business is supplied. If the clause is wrong or the buyer is not registered in time, the ATO treats the price as GST-inclusive and one eleventh of it leaves your hands. We check the clause and the buyer's registration before settlement.",
  },
  {
    q: "Will a restraint of trade actually stop the buyer's competition — or mine?",
    a: "A restraint is enforceable only so far as it is reasonable in area, duration and scope of activity. Cascading restraints (a ladder of alternative radii and periods) are standard in Queensland because a court can read down to the first reasonable rung instead of striking the whole clause. As a seller you are usually the party being restrained, so the drafting decides whether you can work in your industry again.",
  },
  {
    q: "What happens to my employees when I sell?",
    a: "In an asset sale, employment does not transfer automatically — the buyer offers new employment and the parties agree who carries accrued annual leave, long service leave and personal leave. That gets settled as an adjustment to the purchase price. If the buyer does not recognise prior service, redundancy exposure can land on you. It needs to be resolved in the contract, not in the week before settlement.",
  },
  {
    q: "Can I sell the business if I do not own the premises?",
    a: "Yes, but the lease has to be assigned and the landlord has to consent. For retail shop leases the Retail Shop Leases Act disclosure obligations apply and the process has its own timetable — allow two to four weeks minimum for consent, and be aware that in many cases you remain liable under the lease after assignment unless a release is negotiated.",
  },
  {
    q: "How much does it cost to have a solicitor act on the sale?",
    a: `We quote a fixed fee for straightforward small business sales once we have seen the contract or the heads of agreement, so you know the cost before you engage us. Franchises, liquor or food licences, share sales, vendor-financed deals and sales with freehold land attached are quoted individually. Call ${FIRM.phone} and we will scope it on the phone.`,
  },
];

const STAGES = [
  {
    n: "01",
    h: "Before you sign anything",
    b: "Heads of agreement and broker contracts are usually drafted for the buyer or for the broker's commission — not for you. This is the only point where price structure, restraint, GST treatment, deposit terms and post-settlement obligations are still negotiable. Send it to us before it is signed; the review costs nothing.",
  },
  {
    n: "02",
    h: "Contract and disclosure",
    b: "We prepare or negotiate the sale contract, set the due diligence and finance conditions with realistic dates, sort the apportionment of stock, plant and goodwill, and handle retail shop lease disclosure where the premises are leased.",
  },
  {
    n: "03",
    h: "Due diligence and conditions",
    b: "Buyers will ask for everything. We manage what is produced and when, keep confidential material behind a confidentiality deed, and hold the buyer to the deadlines in the contract rather than letting the conditions drift.",
  },
  {
    n: "04",
    h: "Lease, licences and employees",
    b: "Landlord consent to assign, transfer of liquor, food or trade licences, franchisor consent where applicable, and agreement on employee entitlements and the leave adjustment.",
  },
  {
    n: "05",
    h: "Settlement and after",
    b: "Stocktake, adjustments, release of PPSR security interests over your plant, payout of any equipment finance, transfer of business names and domains, and the handover and training period. We also make sure any release of your lease liability is actually documented.",
  },
];

const RISKS = [
  ["GST added to the price", "A defective going-concern clause or an unregistered buyer turns a $1.1m sale into a $1m sale."],
  ["An unenforceable restraint", "Or worse, from a seller's side, one so wide that you cannot work in your own industry."],
  ["Leave liabilities you did not price", "Accrued long service leave on long-serving staff can run to tens of thousands."],
  ["Ongoing lease liability", "Assignment without a release leaves you exposed if the buyer defaults years later."],
  ["Unreleased PPSR interests", "Financed plant sold as unencumbered creates a warranty breach after settlement."],
  ["Earn-outs and holdbacks with no teeth", "Deferred money you cannot enforce is not part of the price."],
];

export const Route = createFileRoute("/selling-a-business-queensland")({
  head: () => ({
    meta: [
      { title: fitTitle(`Selling a Business in Queensland | Seller's Lawyer | ${FIRM.name}`) },
      {
        name: "description",
        content:
          fitDescription("Solicitor-led advice for Queensland business sellers: contract, GST going concern, restraint of trade, lease assignment, employees, vendor finance and settlement. Free contract review."),
      },
      { property: "og:title", content: fitTitle(`Selling a Business in Queensland | ${FIRM.name}`) },
      {
        property: "og:description",
        content:
          fitDescription("What actually decides whether your business sale was a good deal — the contract terms. Fixed-fee, solicitor-led, Sunshine Coast and Moreton Bay."),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "/selling-a-business-queensland" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "/selling-a-business-queensland" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: SellingBusinessPage,
});

function SellingBusinessPage() {
  return (
    <>
      <PageHero
        eyebrow="Business & commercial"
        title="Selling a business in Queensland"
        lead="You only sell it once. The price is agreed early and rarely changes — what changes the outcome is the contract: the GST clause, the restraint, the lease, the employees, and whether any of the money is deferred. We act for sellers across the Sunshine Coast, Moreton Bay and Wide Bay, and we review your contract before you sign at no charge."
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <h2 className="font-serif text-3xl text-foreground">The sale, stage by stage</h2>
        <div className="mt-8 space-y-8">
          {STAGES.map((s) => (
            <div key={s.n} className="border-l border-border pl-6">
              <div className="font-serif text-sm tracking-widest text-primary">{s.n}</div>
              <h3 className="mt-2 font-serif text-xl text-foreground">{s.h}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.b}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-20 font-serif text-3xl text-foreground">
          Where sellers lose money after the price is agreed
        </h2>
        <ul className="mt-6 grid gap-3 sm:grid-cols-2">
          {RISKS.map(([h, b]) => (
            <li key={h} className="rounded-sm border border-border bg-card px-4 py-4">
              <div className="text-sm font-semibold text-foreground">{h}</div>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{b}</p>
            </li>
          ))}
        </ul>

        <div className="mt-20 rounded-sm border border-border bg-secondary/50 p-7">
          <h2 className="font-serif text-2xl text-foreground">
            Selling with vendor finance
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            More Queensland business sales now settle with part of the price left outstanding — a
            deferred balance, an earn-out tied to trading performance, or a straight vendor loan repaid
            over one to three years. It widens your buyer pool and can improve the headline price, but
            it means you have handed over the business and are relying on the buyer to keep paying for
            it. That is a credit decision, not just a legal one.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            If you are going to defer part of the price, the security has to be built into the
            documents at contract stage: a written loan agreement with clear default and acceleration
            terms, a registered PPSR security interest over the business assets, personal guarantees
            from the directors behind the buying entity, restrictions on the buyer selling on or
            further encumbering the business, and a step-in or resumption mechanism if the payments
            stop. Retention of title over key plant and a charge over any real property the guarantors
            own are worth pushing for.
          </p>
          <div className="mt-5 flex flex-wrap gap-4 text-sm font-semibold">
            <Link
              to="/guides/$slug"
              params={{ slug: "vendor-finance-when-selling-a-business" }}
              className="text-primary underline"
            >
              Read the full vendor finance guide
            </Link>
          </div>
        </div>

        <div className="mt-16 rounded-sm border border-border bg-card p-7">
          <h2 className="font-serif text-2xl text-foreground">Before you sign</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Our plain-English material covers the whole transaction — due diligence, the contract, lease
            assignment, employees, GST and going concern, restraints and settlement.
          </p>
          <div className="mt-5 flex flex-col gap-3 text-sm font-semibold">
            <Link
              to="/guides/$slug"
              params={{ slug: "buying-or-selling-a-business-in-queensland" }}
              className="text-primary underline"
            >
              Buying or selling a business in Queensland
            </Link>
            <Link to="/business-sale-checklist" className="text-primary underline">
              Download the due diligence checklist
            </Link>
            <Link to="/retail-shop-leases" className="text-primary underline">
              Retail shop leases and lease assignment
            </Link>
            <Link to="/business-sales" className="text-primary underline">
              Buying a business — what we do for buyers
            </Link>
          </div>
        </div>

        <PeopleStrip slugs={["michael-klein", "erin-wilkinson"]} heading="Who acts on your sale" />

        <h2 className="mt-16 font-serif text-3xl text-foreground">
          Questions Queensland business sellers ask
        </h2>
        <div className="mt-8">
          <FaqList items={FAQS} />
        </div>

        <div className="mt-16">
          <EnquiryForm heading="Get a fixed-fee quote on your business sale" />
        </div>
      </section>

      <CtaBand
        title="Have a contract or heads of agreement in front of you?"
        body={`Send it through before you sign — the review is free — or call ${FIRM.phone}.`}
      />
      <div className="h-20" />
    </>
  );
}
